import type { ReactNode } from 'react';
import { TrendingDown, TrendingUp, Minus, AlertCircle, BarChart3, type LucideIcon } from 'lucide-react';
import type { Kpi as StatsKpi } from '../../lib/api';
import { formatMoney, formatHours, cn } from '../../lib/utils';
import { Card, EmptyState, ICON, Kpi, Skeleton } from '../ui';

export const RATE_LABELS: Record<string, string> = {
  regular: 'Regulier',
  avond: 'Avond +10%',
  nacht: 'Nacht +10%',
  weekend: 'Weekend +35%',
  feest: 'Feestdag +50%',
  avond_feest: 'Avond + feestdag',
  nacht_feest: 'Nacht + feestdag',
  weekend_feest: 'Weekend + feestdag',
  oudjaar: 'Oudejaarsavond 100%',
  overwerk: 'Overwerk +50%',
  meerwerk: 'Meerwerk',
  verlof: 'Verlof',
  ziekte: 'Ziekte',
};

export const RATE_COLORS: Record<string, string> = {
  regular: '#3b82f6',
  avond: '#8b5cf6',
  nacht: '#6366f1',
  weekend: '#f59e0b',
  feest: '#ef4444',
  avond_feest: '#ec4899',
  nacht_feest: '#a855f7',
  weekend_feest: '#f97316',
  oudjaar: '#dc2626',
  overwerk: '#10b981',
  meerwerk: '#14b8a6',
  verlof: '#eab308',
  ziekte: '#f43f5e',
};

/** Verlofsoorten: zelfde tinten als de dienstsoorten (verlof groen, ziek rood), rest neutraal. */
export const LEAVE_COLORS: Record<string, string> = {
  vakantie: '#34d399',
  feestdagverlof: '#34d399',
  bijzonder_verlof: '#8B97A6',
  buitengewoon: '#8B97A6',
  buitengewoon_verlof: '#8B97A6',
  ziekte: '#fb7185',
  work: '#CBD3DC',
};

/** Grafiekpalet (zie stijlgids). */
export const CHART = {
  main: '#CBD3DC',
  muted: '#5A6878',
  money: '#5CCB9A',
  accent: '#F5B841',
  verlof: '#34d399',
  ziek: '#fb7185',
  nacht: '#818cf8',
};

export type KpiFormat = 'money' | 'hours' | 'int' | 'pct' | 'float1' | 'km' | 'eur_per_hour';

/** Uren met één decimaal en een komma: 8,5u. */
export const uur1 = (v: unknown) => `${Number(v ?? 0).toFixed(1).replace('.', ',')}u`;

/** Euro per uur: € 18,42/u. */
export const euPerHour = (n: number) => `€ ${n.toFixed(2).replace('.', ',')}/u`;

export function formatKpi(n: number | null | undefined, fmt: KpiFormat): string {
  const v = n ?? 0;
  switch (fmt) {
    case 'money': return formatMoney(v);
    case 'hours': return formatHours(v);
    case 'int': return Math.round(v).toLocaleString('nl-NL');
    case 'pct': return `${v.toFixed(1).replace('.', ',')}%`;
    case 'float1': return v.toFixed(1).replace('.', ',');
    case 'km': return `${Math.round(v).toLocaleString('nl-NL')} km`;
    case 'eur_per_hour': return euPerHour(v);
  }
}

/** Kerncijfer met het verschil ten opzichte van vorig jaar eronder. */
export function KpiCard({
  label,
  kpi,
  fmt,
  prevYear,
  inverseDelta,
  icon,
}: {
  label: string;
  kpi: StatsKpi | undefined;
  fmt: KpiFormat;
  prevYear?: number;
  inverseDelta?: boolean;
  icon?: LucideIcon;
}) {
  const prev = kpi?.prev ?? 0;
  const delta = kpi?.delta ?? 0;
  const pct = prev !== 0 ? (delta / Math.abs(prev)) * 100 : null;
  const better = inverseDelta ? delta < 0 : delta > 0;
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const color = delta === 0 ? 'text-slate-400' : better ? 'text-money' : 'text-red-300';
  return (
    <Kpi
      icon={icon}
      label={label}
      value={kpi?.value ?? 0}
      format={(n) => formatKpi(n, fmt)}
      hint={
        <span className="flex flex-wrap items-center gap-x-1">
          <span className={cn('t-num inline-flex items-center gap-1', color)}>
            <Icon className={ICON.sm} aria-hidden />
            {formatKpi(Math.abs(delta), fmt)}
            {pct != null && Number.isFinite(pct) ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%)` : ''}
          </span>
          <span>t.o.v. {prevYear ?? 'vorig jaar'}</span>
        </span>
      }
    />
  );
}

/** Rij met kerncijfers: 2 kolommen op mobiel, 4 (of 3) op desktop. */
export function KpiRow({ children, cols = 4 }: { children: ReactNode; cols?: 3 | 4 }) {
  return (
    <div className={cn('grid grid-cols-2 gap-3 sm:gap-4', cols === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}>{children}</div>
  );
}

export function ChartCard({
  title,
  subtitle,
  children,
  height = 280,
  right,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  height?: number;
  right?: ReactNode;
}) {
  return (
    <Card title={title} action={right} className="min-w-0">
      {subtitle && <p className="-mt-2 mb-4 text-sm text-slate-400">{subtitle}</p>}
      <div className="-mx-2" style={{ height }}>
        {children}
      </div>
    </Card>
  );
}

/** Tabel in een kaart tot de rand; scrollt horizontaal op een smal scherm. */
export function TableCard({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children: ReactNode }) {
  return (
    <Card flush title={title} className="min-w-0">
      {sub && <p className="-mt-1 px-4 pb-3 text-sm text-slate-400 sm:px-5">{sub}</p>}
      <div className="overflow-x-auto">{children}</div>
    </Card>
  );
}

export function TabLoading() {
  return (
    <div className="space-y-4 sm:space-y-6" aria-busy="true" aria-label="Laden">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-28" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      <div className="card space-y-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-56 w-full" />
      </div>
    </div>
  );
}

export function TabError({ error }: { error: unknown }) {
  return (
    <Card>
      <EmptyState
        icon={AlertCircle}
        title="De cijfers konden niet laden"
        text={`${(error as Error)?.message ?? 'Onbekende fout'}. Probeer het later opnieuw.`}
      />
    </Card>
  );
}

/** Geen gegevens voor dit jaar: één rustige lege staat. */
export function TabEmpty({ title, text }: { title: string; text?: string }) {
  return (
    <Card>
      <EmptyState icon={BarChart3} title={title} text={text} />
    </Card>
  );
}

export function EmptyChart({ children }: { children: React.ReactNode }) {
  return <div className="h-full flex items-center justify-center px-4 text-center text-sm text-slate-400">{children}</div>;
}

export const TOOLTIP_STYLE = {
  background: '#1A1F26',
  border: '1px solid #252B33',
  borderRadius: 8,
  color: '#E8EAED',
  fontSize: 13,
};

export const TOOLTIP_LABEL_STYLE = { color: '#E8EAED', marginBottom: 2 };
export const TOOLTIP_ITEM_STYLE = { color: '#E8EAED' };
export const TOOLTIP_CURSOR = { fill: '#2A2F37', fillOpacity: 0.5 };
export const LINE_CURSOR = { stroke: '#454B54', strokeDasharray: '3 3' };

export const GRID_STROKE = '#2A2F37';
export const AXIS_STROKE = '#8A919C';
export const AXIS_TICK = { fill: '#8A919C', fontSize: 12 };

/** Animatieduur van alle grafieken (stijlgids: max 300 ms). */
export const ANIM = 300;

/** Gemeenschappelijke as-instellingen: geen aslijn, geen tick-streepjes. */
export const AXIS_PROPS = { tick: AXIS_TICK, tickLine: false, axisLine: false } as const;

export const LEGEND_PROPS = {
  iconType: 'circle' as const,
  iconSize: 8,
  wrapperStyle: { fontSize: 12, color: '#8A919C', paddingTop: 8 },
};

/** Compact euro-label voor assen: € 1,2k */
export function euroTick(v: number): string {
  if (Math.abs(v) >= 1000) return `€${(v / 1000).toFixed(1).replace('.', ',')}k`;
  return `€${Math.round(v)}`;
}

export function euroFull(v: unknown): string {
  return v == null ? '–' : formatMoney(Number(v));
}

/**
 * Y-domein dat niet bij 0 begint: rondt het min/max van de data af op `step`
 * met wat ruimte, zodat schommelingen zichtbaar blijven.
 */
export function paddedDomain(step: number): [(min: number) => number, (max: number) => number] {
  return [
    (min: number) => (Number.isFinite(min) ? Math.floor((min - step / 2) / step) * step : 0),
    (max: number) => (Number.isFinite(max) ? Math.ceil((max + step / 2) / step) * step : step),
  ];
}

export function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
